import {inject, Injectable} from "@angular/core";
import {BehaviorSubject, EMPTY, Observable, of, Subject, throwError, timer} from "rxjs";
import {catchError, distinctUntilChanged, map, retry, shareReplay, switchMap, takeUntil} from "rxjs/operators";
import {ViewerConfigurationService} from "../../../viewer-components/services/viewer-configuration.service";

import {
    GEOFENCING_REQUIRED_DETAIL,
    IGeofencingSearchValidation,
    SearchDisplayMode,
    SearchErrorType,
    SearchFilters,
    SearchPanelEvent,
    SearchParametersQuery,
    SearchQuery,
    SearchResult,
    SearchResultItem,
    SearchState,
    SearchType,
    SearchTypeMode,
} from "../../model";

import {SearchRequestService} from "./requests/search-request.service";
import {GoogleAnalyticsService} from "../../../shared/services/google-analytics.service";
import {
    GoogleAnalyticsConstantsCategory,
    GoogleAnalyticsConstantsEvents,
} from "../../../shared/model/google-analytics-constants";
import {GeoService} from "../../../viewer-components/services/internal-services/geo.service";
import {SearchUtilsService} from "./utils/search-utils.service";
import {GeofencingValidationService} from "./validation/geofencing-validation.service";
import {ToastService} from "../../../viewer-components/services/internal-services/toast.service";
import {TOAST_STATUS} from "../../../viewer-components/model/ToastConstants";
import {IVisionToast} from "../../../viewer-components/interfaces/i-vision-toast";

/**
 * Service for managing search queries, state, and communication with the backend.
 * Handles text, image, location searches, pagination, export, and state updates.
 */
@Injectable({
  providedIn: "root",
})
export class SearchQueryService {
  /** Maximum records allowed for export */
  private readonly MAX_RECORDS = 200;

  private readonly BUFFER_CONTROL_POINT = 500;

  /** Number of retries for the main and control point searches on retryable errors */
  private readonly SEARCH_RETRY_COUNT = 2;

  /** HTTP statuses that must never be retried (the request won't succeed by repeating it) */
  private readonly NON_RETRYABLE_HTTP_STATUSES = [401, 403, 404];

  /** Extra backoff applied on top of a 429 (rate limited) response before retrying */
  private readonly RETRY_RATE_LIMIT_DELAY_MS = 1000;

  private googleAnalyticsService = inject(GoogleAnalyticsService);

  private searchRequestService = inject(SearchRequestService);

  private toastService = inject(ToastService);

  private embeddingsImplementation: string = null;

  /** Search state from backend */
  private searchStateSubject = new BehaviorSubject<SearchState>({
    query: "",
    page: 1,
    pageSize: 100,
    results: [],
    loading: false,
    hasMore: false,
    total: 0,
    maxResults: 100,
    isNewSearch: true,
    searchId: this.generateSearchId(),
  });

  /** Search filters */
  private searchFiltersSubject = new BehaviorSubject<SearchFilters>({
    pageSize: 100,
    maxResults: 500,
  });

  /** Emits when clearSearch() is called to cancel any in-flight search request */
  private cancelSearch$ = new Subject<void>();

  /** Display mode for search panel */
  private searchDisplayModeSubject = new BehaviorSubject<SearchDisplayMode>(SearchDisplayMode.MAP);

  /** Panel events for container communication */
  private searchPanelEventsSubject = new Subject<SearchPanelEvent | null>();

  /** Search type mode state */
  private searchTypeModeSubject = new BehaviorSubject<SearchTypeMode>(SearchTypeMode.TEXT_SEARCH);

  /** Observable for search type mode */
  public searchTypeMode$ = this.searchTypeModeSubject.asObservable().pipe(distinctUntilChanged());

  private scrollToIndexSubject = new BehaviorSubject<number | null>(null);

  /** Observable for components to subscribe to scroll index changes */
  scrollToIndex$ = this.scrollToIndexSubject.asObservable();

  constructor(private viewerConfigurationService: ViewerConfigurationService) {
    this.viewerConfigurationService.embeddingsImplementation$.subscribe((implementation) => {
      this.embeddingsImplementation = implementation;
    });
  }

  /**
   * Emits a new scroll index for synchronization.
   * @param index Index to scroll to
   */
  setScrollToIndex(index: number): void {
    this.scrollToIndexSubject.next(index);
  }

  /** Set the current search type mode */
  setSearchTypeMode(mode: SearchTypeMode): void {
    this.searchTypeModeSubject.next(mode);
  }

  /** Get the current search type mode */
  getCurrentSearchTypeMode(): SearchTypeMode {
    return this.searchTypeModeSubject.value;
  }

  /** Current search query for export/save */
  private currentSearchQuery: SearchQuery | null = null;

  /** Current search ID for pagination consistency */
  private currentSearchId: string | null = null;

  /** Observable for search state */
  public readonly searchState$ = this.searchStateSubject.asObservable();

  /** Observable for search filters */
  public readonly searchFilters$ = this.searchFiltersSubject.asObservable();

  /**
   * Observable for the geofencing precondition of the embeddings search. Emits whenever the
   * applied geofencing changes so search controls can enable/disable themselves reactively.
   */
  public readonly geofencingValidation$: Observable<IGeofencingSearchValidation> = this.searchFilters$.pipe(
    map((filters) => GeofencingValidationService.validateFilters(filters)),
    distinctUntilChanged(
      (previous, current) =>
        previous.valid === current.valid &&
        previous.reason === current.reason &&
        previous.area?.squareMeters === current.area?.squareMeters,
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  /** Observable for display mode */
  public readonly searchDisplayMode$ = this.searchDisplayModeSubject.asObservable().pipe(distinctUntilChanged());

  /**
   * Whether there is an active search whose results would be lost if the geofencing area
   * changed — used to decide whether to warn the user before clearing it.
   * @returns true if there is a query, loaded results, or a known total
   */
  hasActiveSearchResults(): boolean {
    const state = this.searchStateSubject.value;
    return !!state.query?.trim() || state.results.length > 0 || state.total > 0;
  }

  /** Observable for panel events */
  public readonly searchPanelEvents$ = this.searchPanelEventsSubject.asObservable();

  /**
   * Retries a search request on transient failures. Gives up immediately (no retry) on
   * 401/403/404, since repeating the same request won't change that outcome. On a 429
   * (rate limited) response, waits an extra `RETRY_RATE_LIMIT_DELAY_MS` before retrying.
   * @param source$ The search request to retry
   * @returns Observable that retries `source$` according to the rules above
   */
  private withSearchRetries<T>(source$: Observable<T>): Observable<T> {
    return source$.pipe(
      retry({
        count: this.SEARCH_RETRY_COUNT,
        delay: (error) => {
          const status = error?.status;
          if (this.NON_RETRYABLE_HTTP_STATUSES.includes(status)) {
            return throwError(() => error);
          }
          return status === 429 ? timer(this.RETRY_RATE_LIMIT_DELAY_MS) : of(null);
        },
      }),
    );
  }

  /**
   * Executes a search and shows the results as soon as the main search resolves. On the
   * first page, a control point search (using the result with the minimum similarity) then
   * runs in the background to compute the min/max similarity range and assign scores/colors,
   * without blocking the initial display of results (see `scorePending` on SearchState).
   * @param query Search query string
   * @param imageUrl Image URL
   * @param imageBase64 Image base64
   * @param location Location coordinates
   * @param displayQuery Display string for UI
   * @param searchType Type of search
   * @param resetResults Whether to reset results
   * @returns Observable<SearchResult | null>
   */
  private executeSearch(
    query: string,
    imageUrl: string | null,
    imageBase64: string | null,
    location: [number, number] | null,
    displayQuery: string,
    searchType: SearchType,
    resetResults: boolean,
    imagePreview?: string,
  ): Observable<SearchResult | null> {
    // The embeddings API needs a geofencing area and cannot process areas above
    // MAX_GEOFENCING_AREA_SQUARE_KILOMETERS. The search controls already block both cases, but
    // bail out here too so every entry point (Enter key, suggestions, AI tools, load more,
    // export) is covered rather than firing a request that is guaranteed to fail.
    const geofencingValidation = GeofencingValidationService.validateFilters(this.searchFiltersSubject.value);
    if (!geofencingValidation.valid) {
      this.rejectSearchForGeofencing(geofencingValidation);
      return of(null);
    }

    // Cancel any previously in-flight search so a slow, stale response can't
    // arrive after this one and overwrite the newer results.
    this.cancelSearch$.next();

    const currentState = this.searchStateSubject.value;
    const filters = this.searchFiltersSubject.value;
    const page = resetResults ? 1 : currentState.page + 1;
    const searchId = resetResults ? this.generateSearchId() : currentState.searchId;
    if (resetResults) this.currentSearchId = searchId;

    this.updateSearchState({
      query: query || "",
      page,
      results: resetResults ? [] : currentState.results,
      loading: true,
      error: undefined,
      isNewSearch: resetResults,
      searchId,
      pageSize: filters.pageSize,
      maxResults: filters.maxResults,
      // Save all search parameters to state for validation and load more
      searchType: searchType,
      imageUrl: imageUrl || undefined,
      imageBase64: imageBase64 || undefined,
      location: location || undefined,
      // Keep existing wkt and bufferMeters if they exist in filters
      wkt: filters.wkt || undefined,
      bufferMeters: filters["buffer-meter"] || undefined,
      // Initialize pagination tracking
      currentPageSize: filters.pageSize,
      lastCompletePage: resetResults ? 0 : currentState.lastCompletePage,
      loadedQuadkeys: resetResults ? new Set<string>() : currentState.loadedQuadkeys || new Set<string>(),
      // A new search starts with an unknown score range until the control point search resolves
      minScore: resetResults ? undefined : currentState.minScore,
      maxScore: resetResults ? undefined : currentState.maxScore,
      scorePending: resetResults,
    });

    const searchQuery: SearchQuery = {
      query: query?.trim(),
      page,
      imageUrl: imageUrl?.trim(),
      imageBase64: imageBase64,
      imagePreview: imagePreview,
      location: location,
      pageSize: filters.pageSize,
      maxRecords: filters.maxResults,
      searchType: searchType,
    };
    if (filters.wkt) {
      searchQuery.wkt = filters.wkt;
      if (filters["buffer-meter"]) {
        searchQuery.bufferMeters = filters["buffer-meter"];
      }
    }
    this.currentSearchQuery = searchQuery;
    this.trackSearchPerformed(displayQuery, searchQuery.searchType);

    const apiParams = this.convertSearchQueryToApiParams(searchQuery);

    // Main search request
    const mainSearch$ = this.withSearchRetries(this.searchRequestService.search(apiParams)).pipe(
      catchError((error) => {
        this.handleSearchError(error, searchId);
        return EMPTY; // Use EMPTY instead of of(null) to stop stream
      }),
    );

    // If not first page, just do the main search and process as usual
    if (!resetResults) {
      return mainSearch$.pipe(
        map((result) => {
          this.handleSearchSuccess(result, query, page, filters, resetResults, searchId);
          return result;
        }),
        takeUntil(this.cancelSearch$),
      );
    }

    // On first page, show main results immediately; the control point search only
    // refines the score range afterwards, in the background, so it never blocks the first paint.
    return mainSearch$.pipe(
      switchMap((mainResult) => {
        this.handleSearchSuccess(mainResult, query, page, filters, resetResults, searchId);

        if (mainResult?.items?.length) {
          this.refineResultScores(mainResult, apiParams, searchType)
            .pipe(takeUntil(this.cancelSearch$))
            .subscribe();
        } else {
          this.updateSearchState({ scorePending: false });
        }

        return of(mainResult);
      }),
      takeUntil(this.cancelSearch$),
    );
  }

  /**
   * Runs the "control point" search in the background to compute the min/max similarity
   * range and applies score/color to the already-displayed results once it resolves.
   * @param mainResult Result of the main search, already shown to the user
   * @param apiParams API params used for the main search, reused for the control point search
   * @param searchType Type of search performed
   * @returns Observable<void>
   */
  private refineResultScores(
    mainResult: SearchResult,
    apiParams: SearchParametersQuery,
    searchType: SearchType,
  ): Observable<void> {
    // Find the result with minimum similarity
    const minResult = mainResult.items.reduce((min, curr) =>
      (curr.similarity ?? 0) < (min.similarity ?? 0) ? curr : min,
    );
    const controlPointSearch: SearchParametersQuery = {
      ...apiParams,
      wkt: GeoService.pointToWkt([minResult.centroid[0], minResult.centroid[1]]),
      "buffer-meter": this.BUFFER_CONTROL_POINT,
      page: 1, // Always first page for control point
    };

    return this.withSearchRetries(this.searchRequestService.search(controlPointSearch)).pipe(
      catchError((error) => {
        // Log error but continue with only mainResult (score is computed from its items alone)
        console.error("Control points search failed", error);
        return of(null);
      }),
      map((control) => {
        let mainItems = mainResult.items;
        // Skip the first result when the search type is location or image data,
        // as there is a large score gap between the first and second results.
        if (searchType === SearchType.LOCATION || searchType === SearchType.IMAGE_DATA) {
          mainItems = mainItems.slice(1);
        }
        // Combine both results to calculate min/max similarity
        const allItems = [...mainItems, ...(control?.items ?? [])];
        if (!allItems.length) {
          this.updateSearchState({ scorePending: false });
          return;
        }
        const minScore = Math.min(...allItems.map((r) => r.similarity ?? 0));
        const maxScore = Math.max(...mainItems.map((r) => r.similarity ?? 0));

        // Apply score/color to fresh copies of the currently displayed results
        const scoredResults = this.searchStateSubject.value.results.map((item) => ({ ...item }));
        SearchUtilsService.calculateSimilarityValues({
          ...this.searchStateSubject.value,
          results: scoredResults,
          minScore,
          maxScore,
        });
        this.updateSearchState({ results: scoredResults, minScore, maxScore, scorePending: false });
      }),
    );
  }

  /**
   * Performs a text search.
   * @param query Text query string
   * @param resetResults Whether to reset results
   * @returns Observable<SearchResult | null>
   */
  performTextSearch(query: string, resetResults: boolean = true): Observable<SearchResult | null> {
    if (!query?.trim()) {
      console.warn("SearchQueryService: Empty query provided for text search");
      return of(null);
    }
    return this.executeSearch(query, null, null, null, query, SearchType.TEXT, resetResults);
  }

  /**
   * Performs an image URL search.
   * @param imageUrl Image URL string
   * @param resetResults Whether to reset results
   * @returns Observable<SearchResult | null>
   */
  performImageUrlSearch(imageUrl: string, resetResults: boolean = true): Observable<SearchResult | null> {
    if (!imageUrl?.trim()) {
      console.warn("SearchQueryService: Empty image URL provided");
      return of(null);
    }
    const displayQuery = `Image URL: ${imageUrl}`;
    return this.executeSearch(null, imageUrl, null, null, displayQuery, SearchType.IMAGE_URL, resetResults);
  }

  /**
   * Performs an image data (base64) search.
   * @param base64Data Base64 image data string
   * @param resetResults Whether to reset results
   * @returns Observable<SearchResult | null>
   */
  performImageDataSearch(base64Data: string, resetResults: boolean = true, imagePreview?: string): Observable<SearchResult | null> {
    if (!base64Data?.trim()) {
      console.warn("SearchQueryService: Empty image data provided");
      return of(null);
    }
    const displayQuery = `Search by Image data`;
    return this.executeSearch(null, null, base64Data, null, displayQuery, SearchType.IMAGE_DATA, resetResults, imagePreview);
  }

  /**
   * Performs a location search.
   * @param lat Latitude
   * @param lng Longitude
   * @param resetResults Whether to reset results
   * @returns Observable<SearchResult | null>
   */
  performLocationSearch(lat: number, lng: number, resetResults: boolean = true): Observable<SearchResult | null> {
    const displayQuery = `Location: ${lat.toFixed(6)}, ${lng.toFixed(6)}`;
    return this.executeSearch(null, null, null, [lng, lat], displayQuery, SearchType.LOCATION, resetResults);
  }

  /**
   * Loads more results for the current search (pagination).
   * Uses synchronized pagination state from exports for consistency.
   * @returns Observable<SearchResult | null>
   */
  loadMoreResults(): Observable<SearchResult | null> {
    const currentState = this.searchStateSubject.value;

    // Check if there's any valid search parameter based on search type
    const hasValidSearchParam = this.hasValidSearchParameters(currentState);

    // A load-more request cancels any in-flight search via cancelSearch$, which would also
    // cancel the background control point score refinement before it can resolve minScore/maxScore,
    // leaving results with missing ratings permanently. Block until scores are resolved.
    if (!hasValidSearchParam || !currentState.hasMore || currentState.loading || currentState.scorePending) {
      return of(null);
    }

    // Use synchronized pagination: continue from lastCompletePage + 1
    const nextPage = (currentState.lastCompletePage || currentState.page) + 1;

    // Update the current search query with the next page
    if (this.currentSearchQuery) {
      const updatedQuery = {
        ...this.currentSearchQuery,
        page: nextPage,
        pageSize: currentState.currentPageSize || currentState.pageSize || 100,
      };
      this.currentSearchQuery = updatedQuery;

      switch (this.currentSearchQuery.searchType) {
        case SearchType.TEXT:
          return this.performTextSearch(this.currentSearchQuery.query, false);
        case SearchType.IMAGE_URL:
          return this.performImageUrlSearch(this.currentSearchQuery.imageUrl!, false);
        case SearchType.IMAGE_DATA:
          return this.performImageDataSearch(
            this.currentSearchQuery.imageBase64!,
            false,
            this.currentSearchQuery.imagePreview,
          );
        case SearchType.LOCATION:
          if (this.currentSearchQuery.location) {
            const [lng, lat] = this.currentSearchQuery.location;
            return this.performLocationSearch(lat, lng, false);
          }
          break;
      }
    }
    return of(null);
  }

  /**
   * Exports search results with smart reuse of already loaded data
   * Reuses loaded results when possible and fetches additional results only when needed
   * Syncs pagination state with loadMoreResults for consistency
   * @param format Export format (csv, geojson-polygon, geojson-point)
   * @param maxRecords Maximum records to export
   * @param progressCallback Callback to report progress updates
   * @returns Observable<any>
   */
  exportSearchResults(
    format: "csv" | "geojson-polygon" | "geojson-point",
    maxRecords: number,
    progressCallback?: (downloaded: number, total: number) => void,
  ): Observable<any> {
    if (!this.currentSearchQuery) {
      throw new Error("No active search to export");
    }

    const currentState = this.getCurrentSearchState();
    const loadedResults = currentState.results;

    // Clone search parameters to avoid modifying current search state
    const exportQuery: SearchQuery = {
      ...this.currentSearchQuery,
      maxRecords: maxRecords, // Use the requested count, not the search limit
    };

    const apiParams = this.convertSearchQueryToApiParams(exportQuery);

    // Create a search function for additional result fetching
    const searchFn = (params: SearchParametersQuery) => this.searchRequestService.search(params);

    // Enhanced callback to maintain synchronized pagination state
    const updateStateCallback = (additionalResults: SearchResultItem[], lastPageLoaded: number) => {
      if (additionalResults.length > 0) {
        const state = this.getCurrentSearchState();
        const loadedQuadkeys = state.loadedQuadkeys || new Set<string>();

        // Filter duplicates using state's quadkey tracking
        const uniqueAdditionalResults = additionalResults.filter((item) => {
          if (loadedQuadkeys.has(item.quadkey)) {
            return false; // Skip duplicates
          }
          loadedQuadkeys.add(item.quadkey); // Add to tracking
          return true;
        });

        if (uniqueAdditionalResults.length > 0) {
          const updatedResults = [...state.results, ...uniqueAdditionalResults];
          const pageSize = state.currentPageSize || state.pageSize || 100;

          // Calculate the new pagination state
          const totalPages = Math.ceil(updatedResults.length / pageSize);
          const hasMore = updatedResults.length < maxRecords && additionalResults.length === pageSize;

          const newPartialState: Partial<SearchState> = {
            results: updatedResults,
            total: Math.max(state.total, updatedResults.length),
            page: totalPages, // Update to reflect real current page
            lastCompletePage: lastPageLoaded, // Track last complete page from export
            hasMore: hasMore, // Update hasMore based on export results
            loadedQuadkeys: loadedQuadkeys, // Update quadkey tracking
          };
          const newState = { ...currentState, ...newPartialState };
          SearchUtilsService.calculateSimilarityValues(newState);

          this.updateSearchState(newState);
        }
      }
    };

    // Use the enhanced export method from ExportFormatterService
    return this.searchRequestService.export(
      loadedResults,
      searchFn,
      apiParams,
      format,
      maxRecords,
      progressCallback,
      updateStateCallback,
    );
  }

  /**
   * Clears the current search and resets state.
   */
  clearSearch(): void {
    this.cancelSearch$.next();
    const newSearchId = this.generateSearchId();
    this.currentSearchId = newSearchId;
    this.currentSearchQuery = null;
    const filters = this.searchFiltersSubject.value;
    this.searchStateSubject.next({
      query: "",
      page: 1,
      pageSize: filters.pageSize || 100,
      results: [],
      loading: false,
      hasMore: false,
      total: 0,
      maxResults: filters.maxResults || 100,
      isNewSearch: true,
      searchId: newSearchId,
      // Clear all search parameters
      searchType: undefined,
      imageUrl: undefined,
      imageBase64: undefined,
      location: undefined,
      wkt: undefined,
      bufferMeters: undefined,
      // Clear pagination tracking
      lastCompletePage: 0,
      currentPageSize: filters.pageSize || 100,
      loadedQuadkeys: new Set<string>(),
    });
    this.emitPanelEvent({
      action: "clear",
      data: {
        timestamp: new Date(),
      },
    });
  }

  /**
   * Updates search filters and state (pageSize, maxResults, etc.).
   * @param filters Partial filters to update
   */
  updateSearchFiltersAndState(filters: Partial<SearchFilters>): void {
    const updatedFilters = this.updateSearchFilters(filters);
    const currentState = this.searchStateSubject.value;
    if (currentState.query) {
      this.updateSearchState({
        pageSize: updatedFilters.pageSize,
        maxResults: updatedFilters.maxResults,
      });
    }
  }

  updateSearchFilters(filters: Partial<SearchFilters>): SearchFilters {
    const currentFilters = this.searchFiltersSubject.value;
    const updatedFilters = { ...currentFilters, ...filters };
    this.searchFiltersSubject.next(updatedFilters);
    return updatedFilters;
  }

  /**
   * Sets the display mode for the search panel.
   * @param displayMode Search display mode
   */
  setSearchDisplayMode(displayMode: SearchDisplayMode): void {
    this.searchDisplayModeSubject.next(displayMode);
  }

  /**
   * Emits an export event to the panel.
   */
  requestExport(): void {
    this.emitPanelEvent({
      action: "export",
      data: {
        searchQuery: this.currentSearchQuery,
        timestamp: new Date(),
      },
    });
  }

  /**
   * Emits a save search event to the panel.
   */
  requestSaveSearch(): void {
    this.emitPanelEvent({
      action: "save",
      data: {
        searchQuery: this.currentSearchQuery,
        searchState: this.getCurrentSearchState(),
        timestamp: new Date(),
      },
    });
  }

  // ========================================
  // GETTERS
  // ========================================

  /**
   * Gets the current search state.
   * @returns Current SearchState
   */
  getCurrentSearchState(): SearchState {
    return this.searchStateSubject.value;
  }

  /**
   * Gets the current search filters.
   * @returns Current SearchFilters
   */
  getCurrentSearchFilters(): SearchFilters {
    return this.searchFiltersSubject.value;
  }

  /**
   * Gets the current display mode.
   * @returns Current SearchDisplayMode
   */
  getCurrentDisplayMode(): SearchDisplayMode {
    return this.searchDisplayModeSubject.value;
  }

  /**
   * Gets the current search query.
   * @returns Current SearchQuery or null
   */
  getCurrentSearchQuery(): SearchQuery | null {
    return this.currentSearchQuery;
  }

  // ========================================
  // PRIVATE UTILITY METHODS
  // ========================================

  /**
   * Checks if the current state has valid search parameters for any search type.
   * @param state Current search state
   * @returns true if there are valid search parameters
   */
  private hasValidSearchParameters(state: SearchState): boolean {
    // Check based on search type
    switch (state.searchType) {
      case SearchType.TEXT:
        return !!state.query?.trim();
      case SearchType.IMAGE_URL:
        return !!state.imageUrl?.trim();
      case SearchType.IMAGE_DATA:
        return !!state.imageBase64?.trim();
      case SearchType.LOCATION:
        return !!(state.location && state.location.length === 2);
      default:
        // Fallback: check if any search parameter exists
        return !!(
          state.query?.trim() ||
          state.imageUrl?.trim() ||
          state.imageBase64?.trim() ||
          (state.location && state.location.length === 2)
        );
    }
  }

  /**
   * Filters out duplicate results using the state's quadkey tracking.
   * Updates the state's quadkey set with new unique results.
   * @param newResults New results from API call
   * @param currentState Current search state
   * @returns Filtered unique results
   */
  private filterDuplicatesAndUpdateState(
    newResults: SearchResultItem[],
    currentState: SearchState,
  ): SearchResultItem[] {
    const loadedQuadkeys = currentState.loadedQuadkeys || new Set<string>();
    const uniqueResults: SearchResultItem[] = [];

    newResults.forEach((item) => {
      if (!loadedQuadkeys.has(item.quadkey)) {
        loadedQuadkeys.add(item.quadkey);
        uniqueResults.push(item);
      }
    });

    // Update state with new quadkeys
    this.updateSearchState({ loadedQuadkeys });

    return uniqueResults;
  }

  /**
   * Handles successful search result and updates state.
   * @param result SearchResult
   * @param query Search query string
   * @param page Page number
   * @param filters Search filters
   * @param resetResults Whether to reset results
   * @param searchId Search ID
   */
  private handleSearchSuccess(
    result: SearchResult,
    query: string,
    page: number,
    filters: SearchFilters,
    resetResults: boolean,
    searchId: string,
  ): void {
    const currentState = this.searchStateSubject.value;

    // Filter duplicates when not resetting results
    let processedResults = result.items;
    if (!resetResults) {
      processedResults = this.filterDuplicatesAndUpdateState(result.items, currentState);
    } else {
      // For new searches, initialize quadkey tracking
      const loadedQuadkeys = new Set<string>();
      result.items.forEach((item) => loadedQuadkeys.add(item.quadkey));
      this.updateSearchState({ loadedQuadkeys });
    }

    // Clone the already-rendered results instead of reusing their references: calculateSimilarityValues
    // mutates result.score/scoreColor in place below, and with OnPush the result-card children only
    // re-render when their @Input() reference actually changes.
    const newResults = resetResults
      ? result.items
      : [...currentState.results.map((item) => ({ ...item })), ...processedResults];
    // The API reports the total only on page 1, so keep it for the later "Load more" pages.
    // A new search must never inherit the previous search's total.
    const totalRecords = resetResults || page === 1 ? result.totalRecords : currentState.totalRecords;
    // No more pages when: maxResults is reached, the page came back short (small geofence), or
    // the reported total is already covered. The last check is what catches a total that is an
    // exact multiple of the page size, where the final page is full and looks like there's more.
    const loadedByPage = filters.pageSize * page;
    const hasMore =
      loadedByPage < filters.maxResults &&
      result.items.length >= (filters.pageSize || 100) &&
      (totalRecords === undefined || loadedByPage < totalRecords);
    const newSearch = resetResults || page === 1;
    const isNoResults = newSearch && result.items.length === 0;

    // Calculate last complete page based on results
    const pageSize = filters.pageSize || 100;
    const lastCompletePage = result.items.length === pageSize ? page : page - 1;

    // The control point used for the initial minScore can land on a spot that still has real
    // matches nearby, making minScore too high. On every "Load More" batch, re-evaluate it as
    // the min between the current minScore and the worst similarity found in the new batch.
    let minScore = currentState.minScore;
    if (!resetResults && minScore !== undefined && processedResults.length) {
      const newBatchMinSimilarity = Math.min(...processedResults.map((r) => r.similarity ?? 0));
      minScore = Math.min(minScore, newBatchMinSimilarity);
    }

    const newState: SearchState = {
      query,
      page,
      loading: false,
      results: newResults,
      total: result.total,
      totalRecords,
      hasMore,
      error: isNoResults ? "No results found for your search." : undefined,
      errorType: isNoResults ? SearchErrorType.NO_RESULTS : undefined,
      isNewSearch: resetResults,
      searchId,
      pageSize: filters.pageSize,
      maxResults: filters.maxResults,
      minScore,
      maxScore: currentState.maxScore,
      scorePending: currentState.scorePending,
      // Preserve search parameters from current state
      searchType: currentState.searchType,
      imageUrl: currentState.imageUrl,
      imageBase64: currentState.imageBase64,
      location: currentState.location,
      wkt: currentState.wkt,
      bufferMeters: currentState.bufferMeters,
      // Update pagination tracking
      currentPageSize: pageSize,
      lastCompletePage: lastCompletePage,
      loadedQuadkeys: currentState.loadedQuadkeys || new Set<string>(),
    };
    // Score/color normalization needs a resolved min/max range; skip it while the
    // control point search is still refining that range in the background.
    if (!newState.scorePending) {
      SearchUtilsService.calculateSimilarityValues(newState);
    }

    this.updateSearchState(newState);
  }

  /**
   * Handles search errors and updates state.
   * @param error Error object
   * @param searchId Search ID
   */
  private handleSearchError(error: any, searchId: string): void {
    let errorMessage: string;
    let errorType: SearchErrorType;

    if (error.status != null) {
      switch (error.status) {
        case 404:
          errorMessage = "No results found for your search.";
          errorType = SearchErrorType.NOT_FOUND;
          break;
        case 400:
          errorMessage = "Invalid search parameters. Please check your search and try again.";
          errorType = SearchErrorType.UNKNOWN_ERROR;
          break;
        case 401:
        case 403:
          errorMessage = "Authentication error. Please refresh and try again.";
          errorType = SearchErrorType.UNAUTHORIZED;
          break;
        case 0:
        case 408:
          errorMessage = "Search request timed out. Please try again.";
          errorType = SearchErrorType.TIME_OUT_ERROR;
          break;
        case 429:
          errorMessage = "Too many requests. Please wait a moment and try again.";
          errorType = SearchErrorType.UNKNOWN_ERROR;
          break;
        case 500:
        case 502:
        case 503:
        case 504:
          errorMessage = "Server error. Please try again later.";
          errorType = SearchErrorType.SERVER_ERROR;
          break;
        default:
          errorMessage = `Search failed (${error.status}). Please try again.`;
          errorType = SearchErrorType.UNKNOWN_ERROR;
      }
    } else {
      errorMessage = "Network error. Please check your connection and try again.";
      errorType = SearchErrorType.UNKNOWN_ERROR;
    }

    this.updateSearchState({
      loading: false,
      error: errorMessage,
      errorType: errorType, // NUEVO
      searchId,
    });
  }

  /**
   * Returns the current geofencing precondition for the embeddings search.
   * @returns Validation outcome for the geofencing currently applied to the search filters
   */
  getCurrentGeofencingValidation(): IGeofencingSearchValidation {
    return GeofencingValidationService.validateFilters(this.searchFiltersSubject.value);
  }

  /**
   * Rejects a search that cannot succeed because of its geofencing configuration: logs it and
   * notifies the user via toast. Deliberately does NOT set `error`/`errorType` on the search
   * state — the geofencing requirement already has dedicated, persistent UI (search bar
   * warnings, disabled controls, the results landing panel), so surfacing it again as an inline
   * result-panel error would just duplicate that messaging inconsistently.
   * @param validation Failed geofencing validation
   */
  private rejectSearchForGeofencing(validation: IGeofencingSearchValidation): void {
    console.error("SearchQueryService: search blocked by geofencing precondition:", validation.reason);

    // Mirrors handleSearchError: keep whatever is already loaded, but stop offering more pages —
    // every follow-up request would be rejected for the same reason.
    this.updateSearchState({
      loading: false,
      hasMore: false,
    });

    // Same compact top-right warning toast the viewer containers use for their expiry notices.
    // The id doubles as the toast's CSS class (see VisionToastComponent.createToast), so it has
    // to be one of the ids styled in external.scss to get that look.
    // extendedTimeOut: 0 keeps it from being dismissed while the pointer is over it.
    const notification: IVisionToast = {
      id: "toast-warning-geofencing",
      message: validation.reason ?? GEOFENCING_REQUIRED_DETAIL,
      closeButton: true,
      timeout: 8000,
      extendedTimeOut: 0,
      positionClass: "toast-top-right",
      type: TOAST_STATUS.WARNING,
    };
    this.toastService.setNotification(notification);
  }

  /**
   * Injects externally-produced embeddings results into the search state (viewer-handoff
   * plan F2.2): the chat's agent results skip `executeSearch` entirely and land here as
   * ready-made items. Builds a complete, self-consistent `SearchState` — new `searchId`,
   * page 1, `isNewSearch: true`, `hasMore: false` (the agent does not paginate, so "Load
   * more" stays disabled), `total`/`totalRecords` = items.length — and derives
   * `minScore`/`maxScore` from the items' own similarities so the score/color normalization
   * works without the control-point search (a degenerate single-value range is nudged down
   * by an epsilon to keep the normalizer's divide valid). `query`/`wkt`/`searchType` ride
   * along for the panel header and export flows. No GA search events are fired: this is not
   * a user-initiated search.
   */
  loadExternalResults(options: { items: SearchResultItem[]; query?: string; wkt?: string; searchType?: SearchType }): void {
    // Same post-processing the request pipeline applies (SearchRequestService.processResults):
    // the map layer / export / 3D paths read `centroid` unconditionally.
    const items = options.items.map((item) => ({
      ...item,
      ...(item.centroid === undefined ? { centroid: GeoService.wktPolygonCentroid(item.geometry) } : {}),
    }));
    const similarities = items.map((item) => item.similarity).filter((s): s is number => typeof s === "number");
    let minScore = similarities.length ? Math.min(...similarities) : undefined;
    const maxScore = similarities.length ? Math.max(...similarities) : undefined;
    if (minScore !== undefined && minScore === maxScore) minScore -= 0.01;

    const isNoResults = items.length === 0;
    // The summary panel reads the current query to render its header — mirror what
    // executeSearch would have stored for this injected search.
    this.currentSearchQuery = {
      query: options.query ?? "",
      searchType: options.searchType ?? SearchType.TEXT,
      wkt: options.wkt,
      page: 1,
      maxRecords: items.length,
    };
    const newState: SearchState = {
      query: options.query ?? "",
      page: 1,
      pageSize: 100,
      maxResults: 100,
      loading: false,
      hasMore: false,
      results: items,
      total: items.length,
      totalRecords: items.length,
      error: isNoResults ? "No results found for your search." : undefined,
      errorType: isNoResults ? SearchErrorType.NO_RESULTS : undefined,
      isNewSearch: true,
      searchId: this.generateSearchId(),
      minScore,
      maxScore,
      scorePending: false,
      searchType: options.searchType ?? SearchType.TEXT,
      wkt: options.wkt,
      loadedQuadkeys: new Set(items.map((item) => item.quadkey).filter((quadkey): quadkey is string => !!quadkey)),
    };
    if (minScore !== undefined && maxScore !== undefined) {
      SearchUtilsService.calculateSimilarityValues(newState);
    }
    this.updateSearchState(newState);
  }

  /**
   * Updates the search state with provided changes.
   * @param updates Partial SearchState updates
   */
  private updateSearchState(updates: Partial<SearchState>): void {
    const currentState = this.searchStateSubject.value;

    this.searchStateSubject.next({
      ...currentState,
      ...updates,
    });
  }

  /**
   * Emits a panel event for communication.
   * @param event SearchPanelEvent
   */
  private emitPanelEvent(event: SearchPanelEvent): void {
    this.searchPanelEventsSubject.next(event);
  }

  /**
   * Tracks search performed event for analytics.
   * @param query Search query string
   * @param searchType Search type
   */
  private trackSearchPerformed(query: string, searchType: string): void {
    const eventLabel = `${searchType}: ${query}`;
    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.AI_SEARCH_PERFORMED,
      GoogleAnalyticsConstantsCategory.CURATOR_HEADER_BAR,
      true,
      eventLabel,
    );
  }

  /**
   * Converts SearchQuery to API parameters.
   * @param searchQuery SearchQuery object
   * @returns SearchParametersQuery
   */
  private convertSearchQueryToApiParams(searchQuery: SearchQuery): SearchParametersQuery {
    const params: SearchParametersQuery = {
      page: searchQuery.page,
      "page-size": searchQuery.pageSize,
      "total-records": searchQuery.maxRecords,
      include: "collection,geometry,score,quadkey,orientation-source,source.product-type",
    };
    switch (searchQuery.searchType) {
      case SearchType.TEXT:
        if (searchQuery.query) {
          params["search-text"] = searchQuery.query;
        }
        break;
      case SearchType.IMAGE_URL:
        if (searchQuery.imageUrl) {
          params["search-url"] = searchQuery.imageUrl;
        }
        break;
      case SearchType.IMAGE_DATA:
        if (searchQuery.imageBase64) {
          params["search-image"] = searchQuery.imageBase64;
        }
        break;
      case SearchType.LOCATION:
        if (searchQuery.location) {
          const [lng, lat] = searchQuery.location;
          params["search-location"] = GeoService.pointToWkt([lng, lat]);
        }
        break;
    }
    if (searchQuery.wkt) {
      params.wkt = searchQuery.wkt;
      if (searchQuery.bufferMeters) {
        params["buffer-meter"] = searchQuery.bufferMeters;
      }
    }
    if (this.embeddingsImplementation) {
      params.implementation = this.embeddingsImplementation;
    }

    return params;
  }

  /**
   * Generates a unique search ID.
   * @returns Unique search ID string
   */
  private generateSearchId(): string {
    try {
      return crypto.randomUUID();
    } catch (e) {
      return `search_${Date.now()}_${Math.random().toString(36).substring(2)}`;
    }
  }
}
